import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { auth, signInWithEmailAndPassword, sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup } from '../Firebase.tsx'; // Adjust the path as necessary
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, InputAdornment } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import GoogleIcon from '@mui/icons-material/Google'; 
import { allPossibleExtensions } from '../details.tsx';

interface SignInProps {
    switchToSignUp: () => void;
    onSignInSuccess: () => void;
}

const defaultTheme = createTheme();

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function SignIn({ switchToSignUp, onSignInSuccess }: SignInProps) {
    const [openSnackbar, setOpenSnackbar] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<'success' | 'error'>('success');
    const [emailForReset, setEmailForReset] = React.useState('');
    const [openPasswordReset, setOpenPasswordReset] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);

    const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
          return;
      }
      setOpenSnackbar(false);
    };

    const handlePasswordResetClose = () => {
      setOpenPasswordReset(false);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const data = new FormData(event.currentTarget);
      const email = data.get('email') as string;
      const password = data.get('password') as string;

      try {
        if (!allPossibleExtensions.some((ext) => email.endsWith(ext))) {
            setSnackbarMessage('Please enter a valid email address.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } else if (password.length < 6) {
            setSnackbarMessage('Password should be at least 6 characters long.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } else {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const userName = userCredential.user.displayName || 'User';
            setSnackbarMessage(`Welcome back, ${userName}! You have successfully signed in.`);
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
            setTimeout(() => {
                onSignInSuccess();
                // navigate('/main');
            }, 2500);
        }
      } catch (error) {
          setSnackbarMessage('Oops! Something went wrong while signing in. Please check your credentials and try again.');
          setSnackbarSeverity('error');
          setOpenSnackbar(true);
      }
    };

    const handlePasswordReset = async () => {
      if (!emailForReset) {
        setSnackbarMessage('Please enter your email address to reset your password.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        return;
      }

      try {
        await sendPasswordResetEmail(auth, emailForReset);
        setSnackbarMessage('Password reset email sent! Please check your inbox.');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
      } catch (error) {
        setSnackbarMessage('Error sending password reset email. Please try again.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    };

    const handleClickShowPassword = () => {
      setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    };

    const handleGoogleSignIn = async () => {
      const provider = new GoogleAuthProvider();
      try {
        const result = await signInWithPopup(auth, provider);
        const userName = result.user.displayName || 'User';
        setSnackbarMessage(`Welcome back, ${userName}! You have successfully signed in with Google.`);
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
        setTimeout(() => {
          onSignInSuccess();
        }, 2500);
      } catch (error) {
        setSnackbarMessage('Oops! Something went wrong while signing in with Google. Please try again.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    };

    return (
      <ThemeProvider theme={defaultTheme}>
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <Box
            sx={{
              marginTop: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              Sign in
            </Typography>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Sign In
              </Button>
              <Button
                fullWidth
                variant="outlined"
                sx={{ mb: 2 }}
                onClick={handleGoogleSignIn}
                startIcon={<GoogleIcon />} // Use Google icon from MUI
              >
                Sign In with Google
              </Button>
              <Grid container>
                <Grid item xs>
                  <Link href="#" variant="body2" onClick={() => setOpenPasswordReset(true)}>
                    Forgot password?
                  </Link>
                </Grid>
                <Grid item>
                  <Link href="#" variant="body2" onClick={switchToSignUp}>
                    {"Don't have an account? Sign Up"}
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Container>

        <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleSnackbarClose}>
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
        <Dialog
          open={openPasswordReset}
          onClose={handlePasswordResetClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{"Password Reset"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {snackbarMessage}
            </DialogContentText>
            <TextField
              margin="normal"
              required
              fullWidth
              id="emailForReset"
              label="Email Address"
              name="emailForReset"
              autoComplete="email"
              autoFocus
              onChange={(e) => setEmailForReset(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePasswordReset} autoFocus>
              Send Reset Email
            </Button>
            <Button onClick={handlePasswordResetClose}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
}
